import { includes } from 'ramda'
import React from 'react'
import styled from 'styled-components'
import iconDownloadBlue from '~/assets/icons/icon-download-blue.png'
import iconDownloadDodgerBlue from '~/assets/icons/icon-download-dodgerblue.png'
import iconDownloadOrange from '~/assets/icons/icon-download-orange.png'
import iconFile_white from '~/assets/icons/icon-file-white.png'
import { useModalState } from '~/atoms/ModalState'
import { ImageButton, Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import { ImageIcon } from '~/components/ImageIcon'
import Linkify from '~/components/Linkify'
import { emojiParser, isAsciiArt } from '~/constants/parsers'
import { light, spacing } from '~/constants/theme'
import { openInNewTab } from '~/utils/functions'

import { Message } from '@mangovoice/types'

interface RootWrapperProps {
  isFromTeam: boolean
}
const RootWrapper = styled.div<RootWrapperProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ isFromTeam }) => (isFromTeam ? 'flex-end' : 'flex-start')};
`

interface BubbleWrapperProps {
  isFromTeam: boolean
  borderColor: string
  enlargeEmojies: boolean
}
const BubbleWrapper = styled.div<BubbleWrapperProps>`
  word-break: break-word;
  max-width: 55%;
  border: ${(props) => props.borderColor};
  padding: ${spacing.spacingMD};
  padding-left: ${spacing.spacingLG};
  padding-right: ${spacing.spacingLG};
  border-top-right-radius: ${spacing.spacingXL};
  border-bottom-left-radius: ${spacing.spacingXL};
  border-top-left-radius: ${({ isFromTeam }) =>
    isFromTeam ? `${spacing.spacingXL}` : '0px'};
  border-bottom-right-radius: ${({ isFromTeam }) =>
    isFromTeam ? '0px' : `${spacing.spacingXL}`};
  box-sizing: border-box;
  background-color: ${(props) => props.color};
`

const BubbleImageWrapper = styled.div`
  min-height: 25px;
  position: relative;

  &:hover {
    > div {
      display: block;
    }
  }
`

const BubbleImageDownload = styled.div`
  display: none;
  position: absolute;
  top: 0px;
  right: 0;
  width: 100%;
  height: 100%;
`

const BubbleImageOverlay = styled.div`
  position: absolute;
  top: 0px;
  right: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
`

interface BubbleDownloadWrapperProps {
  messageDirection: string
}
const BubbleDownloadWrapper = styled.div<BubbleDownloadWrapperProps>`
  position: absolute;
  ${({ messageDirection }) =>
    messageDirection === 'outbound' ? 'left: -20px;' : 'right: -20px;'};
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  background: white;
  padding: 10px;
  border-radius: 20px;
  border: 1px solid #b4b4cc;
`

const BubbleImage = styled.img`
  max-height: 150px;
  width: auto;
  max-width: 100%;
  border-radius: 5px;
`

const DownloadWrapper = styled.div`
  width: 56px;
  height: 100%;
  display: none;
  align-items: center;
  justify-content: center;
`

const BubbleFileWrapper = styled.div`
  height: 56px;
  display: flex;
  align-items: center;

  &:hover ${DownloadWrapper} {
    display: flex;
  }
`

const BubbleFileDetailWrapper = styled.div`
  width: 144px;
  height: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background-color: ${light.dark600};

  > p {
    margin-left: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

interface MessageBubbleProps {
  message: Message
  fromCurrentUser: boolean
  isFromTeam: boolean
  overrideMaxWidth?: string
}

const MessageBubble = ({
  message,
  fromCurrentUser,
  isFromTeam,
  overrideMaxWidth,
}: MessageBubbleProps) => {
  const {
    previewModal: [, setPreviewModalState],
  } = useModalState()
  const handleOpenPreview = (url, filename) => () => {
    window.gtag('event', 'mms_message_previewed', {})
    setPreviewModalState({
      open: true,
      previewUrl: url,
      previewFilename: filename,
    })
  }

  const handleClickShare = (url) => () => {
    openInNewTab(url)
  }

  const handleDownload = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    url: string,
    filename: string,
  ) => {
    event.stopPropagation()
    window.gtag('event', 'mms_message_downloaded', {})

    const img = await fetch(url, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    })
    const blob = await img.blob()
    const imgUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = imgUrl
    anchor.download = filename.replace('.tif', '.pdf')

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    URL.revokeObjectURL(imgUrl)
  }

  const { body, files } = message

  // Count of emojis for the current message.
  const { enlargeEmojies } = emojiParser(body)
  const textIsAsciiArt = isAsciiArt(body)

  // Assign the corresponding color to the bubble.
  let bubbleColor = `${light.gray100}`
  if (enlargeEmojies) {
    bubbleColor = light.transparent
  } else if (fromCurrentUser) {
    bubbleColor = light.lightblue
  } else if (!isFromTeam) {
    bubbleColor = light.gray100
  }

  return (
    <RootWrapper isFromTeam={isFromTeam}>
      <>
        {body && body !== '' && (
          <BubbleWrapper
            style={{
              maxWidth: overrideMaxWidth ? `${overrideMaxWidth}` : 'auto',
            }}
            enlargeEmojies={enlargeEmojies}
            isFromTeam={isFromTeam}
            color={bubbleColor}
            borderColor={
              fromCurrentUser || enlargeEmojies
                ? `0px solid ${light.transparent}`
                : `1.5px solid ${light.gray400}`
            }
          >
            <Linkify>
              <Text
                fontStyle={FontStyle.Small}
                color={light.dark900}
                style={
                  enlargeEmojies
                    ? {
                        fontSize: '42px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                      }
                    : {
                        fontFamily: textIsAsciiArt ? 'monospace' : 'inherit',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                      }
                }
              >
                {body}
              </Text>
            </Linkify>
          </BubbleWrapper>
        )}
        {files.map(({ name, mime_type, url }) => {
          const isImage = includes('image/', mime_type)
          if (isImage) {
            return (
              <BubbleImageWrapper key={`image-${name}-${url}`}>
                <BubbleImage src={url} />
                <BubbleImageDownload>
                  <BubbleImageOverlay
                    onClick={handleOpenPreview(url, name)}
                    onContextMenu={(e) => {
                      e.preventDefault()
                    }}
                  >
                    <BubbleDownloadWrapper messageDirection={message.direction}>
                      <ImageButton
                        icon={iconDownloadBlue}
                        width="100%"
                        height="20px"
                        imgWidth={20}
                        imgHeight={20}
                        backColor={light.transparent}
                        hoveredColor={light.transparent}
                        activeColor={light.transparent}
                        onClick={(e) => handleDownload(e, url, name)}
                      />
                    </BubbleDownloadWrapper>
                  </BubbleImageOverlay>
                </BubbleImageDownload>
              </BubbleImageWrapper>
            )
          }
          return (
            <BubbleFileWrapper key={`image-${name}-${url}`}>
              <DownloadWrapper>
                <ImageButton
                  icon={iconDownloadDodgerBlue}
                  hoverIcon={iconDownloadOrange}
                  activeIcon={iconDownloadBlue}
                  width="40px"
                  height="40px"
                  imgWidth={25}
                  imgHeight={20}
                  backColor={light.transparent}
                  hoveredColor={light.transparent}
                  activeColor={light.transparent}
                  onClick={handleClickShare(url)}
                />
              </DownloadWrapper>
              <BubbleFileDetailWrapper>
                <ImageIcon icon={iconFile_white} width={30} height={36} />
                <Text
                  fontStyle={FontStyle.ExTiny}
                  text={name}
                  color={fromCurrentUser ? light.gray100 : light.dark900}
                />
              </BubbleFileDetailWrapper>
            </BubbleFileWrapper>
          )
        })}
      </>
    </RootWrapper>
  )
}

export default MessageBubble
